import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Divider,
  ListItemText,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { AppBar, TitlePortal, usePermissions, useRedirect, useLocaleState } from 'react-admin';
import Button from '@mui/material/Button';
import { fetchPendingReviewSummary } from '../../services/pendingEventsService';
import {
  APPLICATION_LIST_PATHS,
  APPLICATION_TYPE_LABELS,
  fetchPendingApplicationsSummary,
} from '../../services/pendingApplicationsService';
import { subscribePendingApplicationsChanged } from '../../services/pendingApplicationsEvents';
import { getEventActionUrl } from '../../utils/eventListUtils';
import {
  buildNotifFingerprint,
  countUnreadNotifs,
  setSeenNotifFingerprint,
} from '../../utils/panelNotifSeen';

const LanguageSwitcher = () => {
  const [locale, setLocale] = useLocaleState();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (newLocale) => {
    setLocale(newLocale);
    handleClose();
  };

  const currentFlag = locale === 'en' ? '🇬🇧' : '🇹🇷';
  const currentText = locale === 'en' ? 'EN' : 'TR';

  return (
    <>
      <Button
        color="inherit"
        onClick={handleOpen}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 13,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.3)',
          mr: 1,
        }}
        startIcon={<span>{currentFlag}</span>}
      >
        {currentText}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem selected={locale === 'tr'} onClick={() => handleSelect('tr')}>
          <span style={{ marginRight: 8 }}>🇹🇷</span> Türkçe
        </MenuItem>
        <MenuItem selected={locale === 'en'} onClick={() => handleSelect('en')}>
          <span style={{ marginRight: 8 }}>🇬🇧</span> English
        </MenuItem>
      </Menu>
    </>
  );
};

const canReview = (permissions) =>
  permissions?.crole === 'admin' || permissions?.crole === 'operator';

/**
 * Ust navbar: bekleyen etkinlik + basvuru bildirimleri (admin/operator).
 */
const CustomAppBar = () => {
  const { permissions } = usePermissions();
  const redirect = useRedirect();
  const [anchorEl, setAnchorEl] = useState(null);
  const [eventTotal, setEventTotal] = useState(0);
  const [eventItems, setEventItems] = useState([]);
  const [applicationTotal, setApplicationTotal] = useState(0);
  const [applicationItems, setApplicationItems] = useState([]);
  const [applicationByType, setApplicationByType] = useState({
    venue: 0,
    organisationCompany: 0,
    salesAgent: 0,
  });
  const [seenTick, setSeenTick] = useState(0);

  const load = useCallback(async () => {
    if (!canReview(permissions)) {
      setEventTotal(0);
      setEventItems([]);
      setApplicationTotal(0);
      setApplicationItems([]);
      setApplicationByType({
        venue: 0,
        organisationCompany: 0,
        salesAgent: 0,
      });
      return;
    }

    try {
      const [events, applications] = await Promise.all([
        permissions?.crole === 'admin'
          ? fetchPendingReviewSummary()
          : Promise.resolve({ total: 0, items: [] }),
        fetchPendingApplicationsSummary(),
      ]);

      setEventTotal(events.total || 0);
      setEventItems(events.items || []);
      setApplicationTotal(applications.total || 0);
      setApplicationItems(applications.items || []);
      setApplicationByType(
        applications.byType || {
          venue: 0,
          organisationCompany: 0,
          salesAgent: 0,
        },
      );
    } catch {
      setEventTotal(0);
      setEventItems([]);
      setApplicationTotal(0);
      setApplicationItems([]);
      setApplicationByType({
        venue: 0,
        organisationCompany: 0,
        salesAgent: 0,
      });
    }
  }, [permissions]);

  useEffect(() => {
    load();
    if (!canReview(permissions)) return undefined;
    const t = setInterval(load, 60000);
    const unsubscribe = subscribePendingApplicationsChanged(load);
    return () => {
      clearInterval(t);
      unsubscribe();
    };
  }, [load, permissions]);

  const open = Boolean(anchorEl);
  const rawTotal = eventTotal + applicationTotal;
  const fingerprint = useMemo(
    () => buildNotifFingerprint({ eventItems, applicationItems }),
    [eventItems, applicationItems],
  );
  // seenTick: okundu yapilinca badge yenilensin
  const badgeTotal = useMemo(
    () => countUnreadNotifs(rawTotal, fingerprint),
    [rawTotal, fingerprint, seenTick],
  );

  const handleMarkAllRead = () => {
    setSeenNotifFingerprint(fingerprint);
    setSeenTick((n) => n + 1);
  };

  if (!canReview(permissions)) {
    return (
      <AppBar color="secondary" toolbar={<TitlePortal />}>
        <LanguageSwitcher />
      </AppBar>
    );
  }

  return (
    <AppBar color="secondary" toolbar={<TitlePortal />}>
      <LanguageSwitcher />
      <IconButton
        color="inherit"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          load();
        }}
        aria-label="notifications"
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={badgeTotal} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { width: 380, maxHeight: 520 } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="subtitle2">Bildirimler ({rawTotal})</Typography>
          <MuiButton
            size="small"
            onClick={handleMarkAllRead}
            disabled={rawTotal === 0 || badgeTotal === 0}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            Tümünü okundu yap
          </MuiButton>
        </Box>
        <Divider />

        {permissions?.crole === 'admin' ? (
          <>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Bekleyen etkinlikler ({eventTotal})
              </Typography>
            </Box>
            {eventItems.length === 0 ? (
              <MenuItem disabled>Bekleyen etkinlik yok</MenuItem>
            ) : (
              eventItems.slice(0, 5).map((ev) => (
                <MenuItem
                  key={`ev-${ev.id}`}
                  onClick={() => {
                    setAnchorEl(null);
                    window.location.href = getEventActionUrl(
                      { id: ev.slug || ev.id, slug: ev.slug, uid: ev.id },
                      'show',
                    );
                  }}
                >
                  <ListItemText
                    primary={ev.name}
                    secondary={ev.createdby || '—'}
                    primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </MenuItem>
              ))
            )}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                redirect('/events-v2?view=pending');
              }}
            >
              Bekleyen etkinlikleri gör
            </MenuItem>
            <Divider />
          </>
        ) : null}

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Bekleyen başvurular ({applicationTotal})
          </Typography>
        </Box>
        {applicationTotal === 0 ? (
          <MenuItem disabled>Bekleyen başvuru yok</MenuItem>
        ) : (
          applicationItems.slice(0, 8).map((item) => (
            <MenuItem
              key={`app-${item.id}`}
              onClick={() => {
                setAnchorEl(null);
                redirect(`/eventCreationApplications/${item.id}/show`);
              }}
            >
              <ListItemText
                primary={item.name || '—'}
                secondary={`${
                  APPLICATION_TYPE_LABELS[item.companyType] || item.companyType
                } · ${item.email || '—'}`}
                primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                secondaryTypographyProps={{ noWrap: true, fontSize: 11 }}
              />
            </MenuItem>
          ))
        )}

        {applicationByType.venue > 0 ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              redirect(APPLICATION_LIST_PATHS.venue);
            }}
          >
            Mekan başvuruları ({applicationByType.venue})
          </MenuItem>
        ) : null}
        {applicationByType.organisationCompany > 0 ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              redirect(APPLICATION_LIST_PATHS.organisationCompany);
            }}
          >
            Organizatör başvuruları ({applicationByType.organisationCompany})
          </MenuItem>
        ) : null}
        {applicationByType.salesAgent > 0 ? (
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              redirect(APPLICATION_LIST_PATHS.salesAgent);
            }}
          >
            Satış ortağı başvuruları ({applicationByType.salesAgent})
          </MenuItem>
        ) : null}
      </Menu>
    </AppBar>
  );
};

export default CustomAppBar;
